import { useEffect, useMemo, useState } from 'react'
import { Alert } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  fetchFinancialAccountsIfNeeded,
  updateFinancialAccount,
} from '@/actions/financialAccountActions'
import { fetchPlaidItemsIfNeeded } from '@/actions/plaidItemActions'
import {
  selectFinancialAccounts,
  selectPlaidItems,
} from '@/selectors/financeSelectors'
import { AccountUsageCard, type AccountUsageRow } from './AccountUsageCard'
import {
  describeAccount,
  isUsageAccount,
  toUsageOutcome,
  toUsageUpdate,
  type AccountUsageAnswers,
} from './accountUsage'
import { seedPrimaryUseAnswer } from '@/features/onboarding/connect-stage/primaryUseAnswers'
import type { WidgetProps } from './types'

type Load = 'loading' | 'failed' | 'ready'

export const AccountUsageWidget = ({ resolved, onOutcome }: WidgetProps) => {
  const dispatch = useAppDispatch()
  const financialAccounts = useReselector(selectFinancialAccounts)
  const plaidItems = useReselector(selectPlaidItems)
  const [load, setLoad] = useState<Load>('loading')
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [answers, setAnswers] = useState<AccountUsageAnswers | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)

  useEffect(() => {
    if (resolved) return undefined
    let cancelled = false
    setLoad('loading')
    // Always refetch: the accounts may have been linked earlier in this chat.
    Promise.all([
      dispatch(fetchFinancialAccountsIfNeeded(true)),
      dispatch(fetchPlaidItemsIfNeeded()),
    ])
      .then(([fetched]) => !cancelled && setLoad(fetched ? 'ready' : 'failed'))
      .catch(() => !cancelled && setLoad('failed'))
    return () => {
      cancelled = true
    }
  }, [dispatch, resolved, loadAttempt])

  const accounts = useMemo(
    () => Object.values(financialAccounts).filter(isUsageAccount),
    [financialAccounts]
  )

  useEffect(() => {
    if (load !== 'ready' || answers !== null) return
    setAnswers(
      Object.fromEntries(
        accounts.map((account) => [account.id, seedPrimaryUseAnswer(account)])
      )
    )
  }, [load, accounts, answers])

  if (resolved) return null
  if (load === 'failed') {
    return (
      <Alert
        variant="error"
        description="Heard couldn't load your accounts right now."
        ctaText="Try again"
        ctaOnClick={() => setLoadAttempt((attempt) => attempt + 1)}
      />
    )
  }
  if (load !== 'ready' || answers === null) return null

  const rows: AccountUsageRow[] = accounts.map((account) => ({
    id: account.id,
    name: account.name,
    detail: describeAccount(account),
    institution:
      account.plaidItemId === null
        ? null
        : (plaidItems[account.plaidItemId] ?? null),
  }))

  const confirm = async () => {
    setSaving(true)
    setSaveFailed(false)
    try {
      const saved = await Promise.all(
        accounts.map((account) =>
          dispatch(
            updateFinancialAccount(
              account.id,
              toUsageUpdate(answers[account.id])
            )
          )
        )
      )
      if (saved.some((account) => !account)) {
        setSaveFailed(true)
        return
      }
      const { text, display } = toUsageOutcome(
        accounts.map((account) => answers[account.id])
      )
      onOutcome(text, display)
    } catch {
      setSaveFailed(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <AccountUsageCard
      accounts={rows}
      answers={answers}
      saving={saving}
      saveFailed={saveFailed}
      onAnswer={(accountId, answer) =>
        setAnswers((current) => ({ ...current, [accountId]: answer }))
      }
      onConfirm={() => void confirm()}
    />
  )
}
